/**
 * AchievementManager - Trophy Room & Achievement Badges System
 * Manages persistent achievement badges in localStorage, live unlock notification toasts,
 * acoustic celebration chimes, and the interactive Trophy Room modal.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { GAME_BRANDS } from '../profile/Profile.js';

export const BADGE_DEFINITIONS = [
  // ==========================================
  // BRAND: Crazy Cribbage (Flagship / Classic / Jokers / Tournaments / Daily)
  // ==========================================

  // --- Category: Match Victories ---
  {
    id: 'first_blood',
    name: 'First Victory',
    desc: 'Win your first full cribbage match against AI or human challenger.',
    icon: '🥇',
    rarity: 'common',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 1
  },
  {
    id: 'victories_5',
    name: 'High Roller',
    desc: 'Win 5 full cribbage matches.',
    icon: '🎖️',
    rarity: 'common',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 5
  },
  {
    id: 'victories_10',
    name: 'Card Shark',
    desc: 'Win 10 full cribbage matches.',
    icon: '🦈',
    rarity: 'rare',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 10
  },
  {
    id: 'victories_25',
    name: 'Board Commander',
    desc: 'Win 25 full cribbage matches.',
    icon: '⚔️',
    rarity: 'rare',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 25
  },
  {
    id: 'victories_50',
    name: 'Master of the Pegs',
    desc: 'Win 50 full cribbage matches.',
    icon: '🛡️',
    rarity: 'epic',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 50
  },
  {
    id: 'victories_100',
    name: 'Centurion',
    desc: 'Win 100 full cribbage matches.',
    icon: '👑',
    rarity: 'legendary',
    category: 'victories',
    brand: 'crazy',
    targetStat: 'totalWins',
    targetVal: 100
  },

  // --- Category: Single Hand Scores ---
  {
    id: 'twenty_pointer',
    name: 'Twenty Pointer',
    desc: 'Score 20 or more points in a single hand.',
    icon: '✋',
    rarity: 'common',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_good',
    name: 'Crazy Good',
    desc: 'Score 30 or more points in a single hand.',
    icon: '😁',
    rarity: 'epic',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'century_club',
    name: 'Century Club',
    desc: 'Score 35 or more points in a single Super Deal hand.',
    icon: '🎩',
    rarity: 'rare',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_amazing',
    name: 'Crazy Amazing',
    desc: 'Score 40 or more points in a single hand.',
    icon: '😁',
    rarity: 'epic',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_awesome',
    name: 'Crazy Awesome',
    desc: 'Score 50 or more points in a single hand.',
    icon: '😁',
    rarity: 'epic',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_legendary',
    name: 'Crazy Legendary',
    desc: 'Score 60 or more points in a single hand.',
    icon: '🌟',
    rarity: 'legendary',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_godlike',
    name: 'Crazy Godlike',
    desc: 'Score 75 or more points in a single hand.',
    icon: '⚡',
    rarity: 'legendary',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'crazy_mythic',
    name: 'Crazy Mythic',
    desc: 'Score 100 or more points in a single explosive hand.',
    icon: '🌌',
    rarity: 'legendary',
    category: 'hands',
    brand: 'crazy'
  },
  {
    id: 'holy_grail',
    name: 'The Holy Grail',
    desc: 'Score the legendary 29-point hand — highest score in standard cribbage.',
    icon: '🏆',
    rarity: 'legendary',
    category: 'hands',
    brand: 'crazy'
  },

  // --- Category: Jokers & Special Rules ---
  {
    id: 'super_joker',
    name: 'Super Joker',
    desc: 'Get 2 jokers in one hand.',
    icon: '😁',
    rarity: 'rare',
    category: 'specials',
    brand: 'crazy'
  },
  {
    id: 'triple_joker',
    name: 'Triple Joker',
    desc: 'Get 3 jokers in a single hand (2-deck mode).',
    icon: '🃏',
    rarity: 'legendary',
    category: 'specials',
    brand: 'crazy'
  },
  {
    id: 'cutthroat_king',
    name: 'Cutthroat King',
    desc: 'Successfully call Muggins on an opponent and steal their forgotten points.',
    icon: '⚡',
    rarity: 'rare',
    category: 'specials',
    brand: 'crazy',
    targetStat: 'mugginsCalls',
    targetVal: 1
  },
  {
    id: 'muggins_5',
    name: 'Eagle Eyes',
    desc: 'Call Muggins 5 times to catch opponents sleeping.',
    icon: '🦅',
    rarity: 'rare',
    category: 'specials',
    brand: 'crazy',
    targetStat: 'mugginsCalls',
    targetVal: 5
  },
  {
    id: 'muggins_15',
    name: 'Muggins Menace',
    desc: 'Call Muggins 15 times.',
    icon: '🕵️',
    rarity: 'epic',
    category: 'specials',
    brand: 'crazy',
    targetStat: 'mugginsCalls',
    targetVal: 15
  },
  {
    id: 'muggins_30',
    name: 'The Master Thief',
    desc: 'Call Muggins 30 times.',
    icon: '💰',
    rarity: 'legendary',
    category: 'specials',
    brand: 'crazy',
    targetStat: 'mugginsCalls',
    targetVal: 30
  },

  // --- Category: Tournament Wins ---
  {
    id: 'tourney_first_win',
    name: 'Table Champion',
    desc: 'Win your first mini-tournament bracket championship.',
    icon: '🏆',
    rarity: 'rare',
    category: 'tournaments',
    brand: 'crazy',
    targetStat: 'tournamentWins',
    targetVal: 1
  },
  {
    id: 'tourney_3_wins',
    name: 'Bracket Dominator',
    desc: 'Win 3 mini-tournament championships.',
    icon: '🥇',
    rarity: 'epic',
    category: 'tournaments',
    brand: 'crazy',
    targetStat: 'tournamentWins',
    targetVal: 3
  },
  {
    id: 'tourney_5_wins',
    name: 'Tournament Veteran',
    desc: 'Win 5 mini-tournament championships.',
    icon: '🎖️',
    rarity: 'epic',
    category: 'tournaments',
    brand: 'crazy',
    targetStat: 'tournamentWins',
    targetVal: 5
  },
  {
    id: 'tourney_10_wins',
    name: 'Grand Tournament Master',
    desc: 'Win 10 mini-tournament championships.',
    icon: '👑',
    rarity: 'legendary',
    category: 'tournaments',
    brand: 'crazy',
    targetStat: 'tournamentWins',
    targetVal: 10
  },
  {
    id: 'tourney_25_wins',
    name: 'Undisputed Table Legend',
    desc: 'Win 25 mini-tournament championships.',
    icon: '🏛️',
    rarity: 'legendary',
    category: 'tournaments',
    brand: 'crazy',
    targetStat: 'tournamentWins',
    targetVal: 25
  },
  {
    id: 'tourney_double_elim',
    name: 'Double Elimination King',
    desc: 'Win a full Double Elimination tournament bracket.',
    icon: '🔁',
    rarity: 'epic',
    category: 'tournaments',
    brand: 'crazy'
  },
  {
    id: 'tourney_8_player',
    name: 'Octo-Champion',
    desc: 'Win an 8-player tournament bracket from start to finish.',
    icon: '🐙',
    rarity: 'legendary',
    category: 'tournaments',
    brand: 'crazy'
  },

  // --- Category: Daily Discard Challenges ---
  {
    id: 'daily_discard_first',
    name: 'Daily Discard Apprentice',
    desc: 'Find the optimal discard in your first Daily Discard puzzle.',
    icon: '🧩',
    rarity: 'common',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardOptimal',
    targetVal: 1
  },
  {
    id: 'daily_discard_5',
    name: 'Daily Discard Specialist',
    desc: 'Achieve 5 optimal Daily Discard puzzle solutions.',
    icon: '💡',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardOptimal',
    targetVal: 5
  },
  {
    id: 'daily_discard_10',
    name: 'Daily Discard Expert',
    desc: 'Achieve 10 optimal Daily Discard puzzle solutions.',
    icon: '🧠',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardOptimal',
    targetVal: 10
  },
  {
    id: 'daily_discard_25',
    name: 'Daily Discard Master',
    desc: 'Achieve 25 optimal Daily Discard puzzle solutions.',
    icon: '🎓',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardOptimal',
    targetVal: 25
  },
  {
    id: 'daily_discard_50',
    name: 'Daily Discard Oracle',
    desc: 'Achieve 50 optimal Daily Discard puzzle solutions.',
    icon: '🔮',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardOptimal',
    targetVal: 50
  },
  {
    id: 'daily_discard_streak_3',
    name: 'Discard Streak (3 Days)',
    desc: 'Solve the Daily Discard optimally 3 consecutive days in a row.',
    icon: '🔥',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardStreak',
    targetVal: 3
  },
  {
    id: 'daily_discard_streak_7',
    name: 'Discard Streak (7 Days)',
    desc: 'Solve the Daily Discard optimally 7 consecutive days in a row.',
    icon: '⚡',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyDiscardStreak',
    targetVal: 7
  },

  // --- Category: Daily Pegging Challenges ---
  {
    id: 'daily_pegging_first',
    name: 'Daily Pegging Sharp',
    desc: 'Find the winning card in your first Daily Pegging scenario.',
    icon: '🎯',
    rarity: 'common',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingCorrect',
    targetVal: 1
  },
  {
    id: 'daily_pegging_5',
    name: 'Daily Pegging Tactician',
    desc: 'Solve 5 Daily Pegging scenarios correctly.',
    icon: '🏹',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingCorrect',
    targetVal: 5
  },
  {
    id: 'daily_pegging_10',
    name: 'Daily Pegging Strategist',
    desc: 'Solve 10 Daily Pegging scenarios correctly.',
    icon: '♟️',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingCorrect',
    targetVal: 10
  },
  {
    id: 'daily_pegging_25',
    name: 'Daily Pegging Grandmaster',
    desc: 'Solve 25 Daily Pegging scenarios correctly.',
    icon: '🎪',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingCorrect',
    targetVal: 25
  },
  {
    id: 'daily_pegging_50',
    name: 'Daily Pegging Virtuoso',
    desc: 'Solve 50 Daily Pegging scenarios correctly.',
    icon: '🌠',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingCorrect',
    targetVal: 50
  },
  {
    id: 'daily_pegging_streak_3',
    name: 'Pegging Streak (3 Days)',
    desc: 'Solve Daily Pegging correctly 3 consecutive days in a row.',
    icon: '🔥',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingStreak',
    targetVal: 3
  },
  {
    id: 'daily_pegging_streak_7',
    name: 'Pegging Streak (7 Days)',
    desc: 'Solve Daily Pegging correctly 7 consecutive days in a row.',
    icon: '⚡',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyPeggingStreak',
    targetVal: 7
  },

  // --- Category: Daily Puzzle Streaks (Both Challenges) ---
  {
    id: 'daily_streak_3',
    name: 'Daily Devotee (3-Day Streak)',
    desc: 'Complete both Daily Discard and Pegging challenges 3 days in a row.',
    icon: '🗓️',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyStreak',
    targetVal: 3
  },
  {
    id: 'daily_streak_7',
    name: 'Daily Champion (7-Day Streak)',
    desc: 'Complete both Daily challenges 7 days in a row.',
    icon: '🌟',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyStreak',
    targetVal: 7
  },
  {
    id: 'daily_streak_14',
    name: 'Daily Legend (14-Day Streak)',
    desc: 'Complete both Daily challenges 14 days in a row.',
    icon: '🏅',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyStreak',
    targetVal: 14
  },
  {
    id: 'daily_streak_30',
    name: 'Daily Immortal (30-Day Streak)',
    desc: 'Complete both Daily challenges 30 days in a row.',
    icon: '🪐',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'dailyStreak',
    targetVal: 30
  },

  // --- Category: Discard Trainer (EV Exploration) ---
  {
    id: 'ev_master',
    name: 'EV Master',
    desc: 'Find the #1 statistically optimal crib discard in the Discard Trainer.',
    icon: '🎯',
    rarity: 'common',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'trainerOptimalDiscards',
    targetVal: 1
  },
  {
    id: 'ev_scholar',
    name: 'Discard Scholar',
    desc: 'Find 10 #1 optimal discards in the Discard Trainer.',
    icon: '📚',
    rarity: 'rare',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'trainerOptimalDiscards',
    targetVal: 10
  },
  {
    id: 'ev_grandmaster',
    name: 'Discard Grandmaster',
    desc: 'Find 25 #1 optimal discards in the Discard Trainer.',
    icon: '🎖️',
    rarity: 'epic',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'trainerOptimalDiscards',
    targetVal: 25
  },
  {
    id: 'ev_guru',
    name: 'EV Oracle',
    desc: 'Find 50 #1 optimal discards in the Discard Trainer.',
    icon: '👁️',
    rarity: 'legendary',
    category: 'daily',
    brand: 'crazy',
    targetStat: 'trainerOptimalDiscards',
    targetVal: 50
  },

  // ==========================================
  // BRAND: Cribbage Clash (481-Hole Marathon & Track Challenges)
  // ==========================================
  {
    id: 'clash_conqueror',
    name: 'Clash Conqueror',
    desc: 'Emerge victorious on the epic 481-hole circular marathon track.',
    icon: '🌀',
    rarity: 'common',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 1
  },
  {
    id: 'clash_veteran',
    name: 'Clash Veteran',
    desc: 'Win 3 Cribbage Clash marathon matches.',
    icon: '🏎️',
    rarity: 'rare',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 3
  },
  {
    id: 'clash_master',
    name: 'Clash Master',
    desc: 'Win 10 Cribbage Clash marathon matches.',
    icon: '🏁',
    rarity: 'epic',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 10
  },
  {
    id: 'clash_legend',
    name: 'Clash Legend',
    desc: 'Win 25 Cribbage Clash marathon matches.',
    icon: '⚡',
    rarity: 'legendary',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 25
  },
  {
    id: 'clash_speedster',
    name: 'Speed Demon',
    desc: 'Win 50 Cribbage Clash marathon matches.',
    icon: '🚀',
    rarity: 'legendary',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 50
  },
  {
    id: 'clash_ring_ruler',
    name: 'Master of the Track',
    desc: 'Win 100 Cribbage Clash marathon matches.',
    icon: '👑',
    rarity: 'legendary',
    category: 'specials',
    brand: 'clash',
    targetStat: 'clashWins',
    targetVal: 100
  },

  // ==========================================
  // BRAND: Pints & Pegs (Pub Matches, Pints & Zero Drinks)
  // ==========================================
  {
    id: 'pub_first_pint',
    name: 'First Round',
    desc: 'Win your first Pints & Pegs pub match.',
    icon: '🍺',
    rarity: 'common',
    category: 'specials',
    brand: 'pints',
    targetStat: 'pintsWins',
    targetVal: 1
  },
  {
    id: 'pub_legend',
    name: 'Pub Legend',
    desc: 'Finish a Pints & Pegs match with zero drink penalties taken.',
    icon: '🍻',
    rarity: 'rare',
    category: 'specials',
    brand: 'pints'
  },
  {
    id: 'pub_regular',
    name: 'Pub Regular',
    desc: 'Win 5 Pints & Pegs pub matches.',
    icon: '🍺',
    rarity: 'rare',
    category: 'specials',
    brand: 'pints',
    targetStat: 'pintsWins',
    targetVal: 5
  },
  {
    id: 'pub_champion',
    name: 'Pub Champion',
    desc: 'Win 15 Pints & Pegs pub matches.',
    icon: '🏆',
    rarity: 'epic',
    category: 'specials',
    brand: 'pints',
    targetStat: 'pintsWins',
    targetVal: 15
  },
  {
    id: 'pub_master',
    name: 'Tavern Master',
    desc: 'Win 25 Pints & Pegs pub matches.',
    icon: '👑',
    rarity: 'legendary',
    category: 'specials',
    brand: 'pints',
    targetStat: 'pintsWins',
    targetVal: 25
  },
  {
    id: 'pub_tavern_king',
    name: 'King of the Tavern',
    desc: 'Win 50 Pints & Pegs pub matches.',
    icon: '🏰',
    rarity: 'legendary',
    category: 'specials',
    brand: 'pints',
    targetStat: 'pintsWins',
    targetVal: 50
  },

  // ==========================================
  // BRAND: Skunked & Stripped (Skunks & High Stakes Matches)
  // ==========================================
  {
    id: 'adult_first_strip',
    name: 'Strip Showdown',
    desc: 'Win your first Skunked & Stripped showdown match.',
    icon: '💋',
    rarity: 'common',
    category: 'victories',
    brand: 'adult',
    targetStat: 'adultWins',
    targetVal: 1
  },
  {
    id: 'skunk_master',
    name: 'Skunk Master',
    desc: 'Defeat an opponent by 31 or more points.',
    icon: '🦨',
    rarity: 'epic',
    category: 'victories',
    brand: 'adult',
    targetStat: 'totalSkunks',
    targetVal: 1
  },
  {
    id: 'double_skunk',
    name: 'Double Skunk Destroyer',
    desc: 'Defeat an opponent by 61 or more points (Double Skunk).',
    icon: '🦨',
    rarity: 'epic',
    category: 'victories',
    brand: 'adult',
    targetStat: 'totalDoubleSkunks',
    targetVal: 1
  },
  {
    id: 'skunk_5',
    name: 'Skunk Hunter',
    desc: 'Skunk opponents 5 times.',
    icon: '🎯',
    rarity: 'rare',
    category: 'victories',
    brand: 'adult',
    targetStat: 'totalSkunks',
    targetVal: 5
  },
  {
    id: 'skunk_10',
    name: 'Skunk Overlord',
    desc: 'Skunk opponents 10 times.',
    icon: '☠️',
    rarity: 'epic',
    category: 'victories',
    brand: 'adult',
    targetStat: 'totalSkunks',
    targetVal: 10
  },
  {
    id: 'skunk_25',
    name: 'Reign of the Skunk',
    desc: 'Skunk opponents 25 times.',
    icon: '🌪️',
    rarity: 'legendary',
    category: 'victories',
    brand: 'adult',
    targetStat: 'totalSkunks',
    targetVal: 25
  },
  {
    id: 'adult_high_roller',
    name: 'Daredevil',
    desc: 'Win 10 Skunked & Stripped matches.',
    icon: '🔥',
    rarity: 'epic',
    category: 'victories',
    brand: 'adult',
    targetStat: 'adultWins',
    targetVal: 10
  },
  {
    id: 'adult_bare_minimum',
    name: 'Stripped Clean',
    desc: 'Win 25 Skunked & Stripped matches.',
    icon: '✨',
    rarity: 'legendary',
    category: 'victories',
    brand: 'adult',
    targetStat: 'adultWins',
    targetVal: 25
  }
];

export class AchievementManager {
  static STORAGE_KEY = 'crazyCribbage.achievements';
  static STATS_KEY = 'crazyCribbage.achievementStats';

  /**
   * Loads unlocked achievements map from storage.
   * @param {Object} [storage=null]
   * @returns {Object<string, { unlockedAt: number }>}
   */
  static loadUnlocked(storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return {};
    try {
      const raw = s.getItem(AchievementManager.STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Persists unlocked achievements map to storage.
   * @param {Object} data
   * @param {Object} [storage=null]
   */
  /**
   * Persists unlocked achievements map to storage and syncs with user profile.
   * @param {Object} data
   * @param {Object} [storage=null]
   */
  static saveUnlocked(data, storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return;
    try {
      s.setItem(AchievementManager.STORAGE_KEY, JSON.stringify(data));
      // Keep profile synced
      const prof = (typeof window !== 'undefined' && window.Profile)
        ? window.Profile.getInstance(storage)
        : null;
      if (prof && typeof prof.syncTrophiesFromUnlocked === 'function') {
        prof.syncTrophiesFromUnlocked(data, storage);
      }
    } catch (e) {}
  }

  /**
   * Retrieves persistent achievement tracking statistics, automatically syncing
   * with DailyPuzzleManager stats, tournament history, match win records, and player profile.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getStats(storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    const defaultStats = {
      totalWins: 0,
      totalSkunks: 0,
      totalDoubleSkunks: 0,
      clashWins: 0,
      pintsWins: 0,
      adultWins: 0,
      crazyWins: 0,
      tournamentWins: 0,
      dailyDiscardOptimal: 0,
      dailyDiscardStreak: 0,
      dailyPeggingCorrect: 0,
      dailyPeggingStreak: 0,
      dailyStreak: 0,
      trainerOptimalDiscards: 0,
      mugginsCalls: 0
    };
    if (!s) return defaultStats;

    try {
      const raw = s.getItem(AchievementManager.STATS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const merged = { ...defaultStats, ...parsed };

      // Sync with DailyPuzzleManager if higher
      try {
        const dpRaw = s.getItem('cc_daily_puzzle_stats');
        if (dpRaw) {
          const dp = JSON.parse(dpRaw);
          if (dp.perfectDiscards > merged.dailyDiscardOptimal) merged.dailyDiscardOptimal = dp.perfectDiscards;
          if (dp.peggingWins > merged.dailyPeggingCorrect) merged.dailyPeggingCorrect = dp.peggingWins;
          if (dp.currentStreak > merged.dailyStreak) merged.dailyStreak = dp.currentStreak;
          if (dp.bestStreak > merged.dailyStreak) merged.dailyStreak = Math.max(merged.dailyStreak, dp.bestStreak);
        }
      } catch (e) {}

      // Sync with Tournament history if higher
      try {
        const thRaw = s.getItem('crazyCribbage.tournamentHistory');
        if (thRaw) {
          const th = JSON.parse(thRaw);
          if (Array.isArray(th) && th.length > merged.tournamentWins) {
            merged.tournamentWins = Math.max(merged.tournamentWins, th.length);
          }
        }
      } catch (e) {}

      // Sync with Profile per-brand statistics if higher
      try {
        const prof = (typeof window !== 'undefined' && window.Profile)
          ? window.Profile.getInstance(storage)
          : null;
        if (prof && prof.stats?.byBrand) {
          const pb = prof.stats.byBrand;
          if (pb.clash?.wins > merged.clashWins) merged.clashWins = pb.clash.wins;
          if (pb.pints?.wins > merged.pintsWins) merged.pintsWins = pb.pints.wins;
          if (pb.adult?.wins > merged.adultWins) merged.adultWins = pb.adult.wins;
          if (pb.crazy?.wins > merged.crazyWins) merged.crazyWins = pb.crazy.wins;
          if ((prof.stats.wins || 0) > merged.totalWins) merged.totalWins = prof.stats.wins;
          if ((prof.stats.skunkWins || 0) > merged.totalSkunks) merged.totalSkunks = prof.stats.skunkWins;
        }
      } catch (e) {}

      return merged;
    } catch (e) {
      return defaultStats;
    }
  }

  /**
   * Persists updated tracking statistics to storage.
   * @param {Object} stats
   * @param {Object} [storage=null]
   */
  static saveStats(stats, storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return;
    try {
      s.setItem(AchievementManager.STATS_KEY, JSON.stringify(stats));
    } catch (e) {}
  }

  /**
   * Increments a numeric statistic and evaluates any milestone badges.
   * @param {string} key
   * @param {number} [amount=1]
   * @param {Object} [options={}]
   * @returns {number} Updated stat value
   */
  static incrementStat(key, amount = 1, options = {}) {
    if (!key) return 0;
    const stats = AchievementManager.getStats(options.storage);
    stats[key] = (stats[key] || 0) + amount;
    AchievementManager.saveStats(stats, options.storage);
    AchievementManager.checkMilestoneBadges(stats, options);
    return stats[key];
  }

  /**
   * Sets a numeric statistic (monotonic increase) and evaluates milestone badges.
   * @param {string} key
   * @param {number} val
   * @param {Object} [options={}]
   * @returns {number} Updated stat value
   */
  static setStat(key, val, options = {}) {
    if (!key) return 0;
    const stats = AchievementManager.getStats(options.storage);
    stats[key] = Math.max(stats[key] || 0, Number(val) || 0);
    AchievementManager.saveStats(stats, options.storage);
    AchievementManager.checkMilestoneBadges(stats, options);
    return stats[key];
  }

  /**
   * Evaluates all milestone badges with targetStat against current stats.
   * @param {Object} [stats=null]
   * @param {Object} [options={}]
   */
  static checkMilestoneBadges(stats = null, options = {}) {
    const s = stats || AchievementManager.getStats(options.storage);
    BADGE_DEFINITIONS.forEach(badge => {
      if (badge.targetStat && badge.targetVal) {
        if ((s[badge.targetStat] || 0) >= badge.targetVal) {
          AchievementManager.unlock(badge.id, options);
        }
      }
    });
  }

  /**
   * Records a tournament championship victory and awards tournament badges.
   * @param {Object} [tourneyState=null]
   * @param {Object} [options={}]
   */
  static recordTournamentWin(tourneyState = null, options = {}) {
    AchievementManager.incrementStat('tournamentWins', 1, options);
    AchievementManager.unlock('tourney_first_win', options);
    if (tourneyState) {
      if (tourneyState.format === 'double') {
        AchievementManager.unlock('tourney_double_elim', options);
      }
      if (tourneyState.playerCount === 8) {
        AchievementManager.unlock('tourney_8_player', options);
      }
    }
  }

  /**
   * Computes consecutive optimal discard and correct pegging streaks from DailyPuzzleManager history.
   * @param {Object} dailyStats
   * @param {Object} [options={}]
   */
  static checkDailyStreaks(dailyStats, options = {}) {
    if (!dailyStats) return;
    if (dailyStats.currentStreak > 0) {
      AchievementManager.setStat('dailyStreak', dailyStats.currentStreak, options);
    }
    if (dailyStats.perfectDiscards > 0) {
      AchievementManager.setStat('dailyDiscardOptimal', dailyStats.perfectDiscards, options);
    }
    if (dailyStats.peggingWins > 0) {
      AchievementManager.setStat('dailyPeggingCorrect', dailyStats.peggingWins, options);
    }

    const history = dailyStats.history;
    if (!history) return;

    // Check consecutive days ending today/yesterday for discard and pegging
    let currentDiscardStreak = 0;
    let currentPeggingStreak = 0;
    const now = new Date();

    for (let i = 0; i < 60; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const entry = history[key];
      if (entry && entry.discard && entry.discard.grade === 'optimal') {
        currentDiscardStreak++;
      } else if (i === 0 && (!entry || !entry.discard)) {
        continue; // Today not played yet
      } else {
        break;
      }
    }

    for (let i = 0; i < 60; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const entry = history[key];
      if (entry && entry.pegging && entry.pegging.isCorrect) {
        currentPeggingStreak++;
      } else if (i === 0 && (!entry || !entry.pegging)) {
        continue; // Today not played yet
      } else {
        break;
      }
    }

    if (currentDiscardStreak > 0) {
      AchievementManager.setStat('dailyDiscardStreak', currentDiscardStreak, options);
    }
    if (currentPeggingStreak > 0) {
      AchievementManager.setStat('dailyPeggingStreak', currentPeggingStreak, options);
    }
  }

  /**
   * Checks if an achievement badge is already unlocked.
   * @param {string} id
   * @param {Object} [storage=null]
   * @returns {boolean}
   */
  static isUnlocked(id, storage = null) {
    const data = AchievementManager.loadUnlocked(storage);
    return Boolean(data && data[id]);
  }

  /**
   * Unlocks an achievement badge, plays reward audio, syncs to user profile, and displays unlock toast.
   * @param {string} id
   * @param {Object} [options={}]
   * @returns {boolean} True if freshly unlocked, false if already achieved
   */
  static unlock(id, options = {}) {
    if (!id) return false;
    const badge = BADGE_DEFINITIONS.find(b => b.id === id);
    if (!badge) return false;

    const storage = options.storage || null;
    const data = AchievementManager.loadUnlocked(storage);
    if (data[id]) {
      return false; // Already unlocked
    }

    data[id] = { unlockedAt: Date.now() };
    AchievementManager.saveUnlocked(data, storage);

    // Sync directly into User Profile
    try {
      const prof = (typeof window !== 'undefined' && window.Profile)
        ? window.Profile.getInstance(storage)
        : null;
      if (prof && typeof prof.recordTrophy === 'function') {
        prof.recordTrophy(id, badge.brand, storage);
      }
    } catch (_) {}

    // Audio chime
    const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    if (sfx && typeof sfx.achievement === 'function') {
      try { sfx.achievement(); } catch (e) {}
    }

    // Live unlock toast
    AchievementManager.showUnlockToast(badge);
    return true;
  }

  /**
   * Displays animated unlock notification toast.
   * @param {Object} badge
   */
  static showUnlockToast(badge) {
    if (typeof document === 'undefined') return;

    let toastContainer = document.getElementById('achievementToastContainer');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'achievementToastContainer';
      toastContainer.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 100000;
        display: flex;
        flex-direction: column;
        gap: 10px;
        pointer-events: none;
      `;
      document.body.appendChild(toastContainer);
    }

    const toast = document.createElement('div');
    toast.className = 'achievement-toast';
    toast.style.cssText = `
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 14px 20px;
      background: rgba(18, 14, 28, 0.94);
      border: 1.5px solid #eab308;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.6), 0 0 15px rgba(234, 179, 8, 0.35);
      border-radius: 12px;
      color: #f8fafc;
      font-family: inherit;
      max-width: 380px;
      backdrop-filter: blur(12px);
      transform: translateX(120%);
      opacity: 0;
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease;
      pointer-events: auto;
    `;

    toast.innerHTML = `
      <div style="font-size: 2.2rem; filter: drop-shadow(0 2px 6px rgba(234, 179, 8, 0.5)); flex-shrink: 0;">
        ${badge.icon}
      </div>
      <div style="flex: 1; min-width: 0;">
        <div style="font-size: 0.72rem; font-weight: 800; letter-spacing: 0.1em; color: #facc15; text-transform: uppercase;">
          🏆 Achievement Unlocked
        </div>
        <div style="font-size: 1.05rem; font-weight: 700; color: #ffffff; margin-top: 2px;">
          ${badge.name}
        </div>
        <div style="font-size: 0.8rem; color: #cbd5e1; margin-top: 2px; line-height: 1.3;">
          ${badge.desc}
        </div>
      </div>
    `;

    toastContainer.appendChild(toast);

    // Slide in
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(0)';
      toast.style.opacity = '1';
    });

    // Dismiss after 4.8s
    setTimeout(() => {
      toast.style.transform = 'translateX(120%)';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 450);
    }, 4800);
  }

  /**
   * Returns list of all badges merged with their unlock metadata and numeric progress.
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  static getAchievements(storage = null) {
    const unlocked = AchievementManager.loadUnlocked(storage);
    const stats = AchievementManager.getStats(storage);
    return BADGE_DEFINITIONS.map(badge => {
      const isUn = Boolean(unlocked[badge.id]);
      const current = badge.targetStat ? (stats[badge.targetStat] || 0) : null;
      const target = badge.targetVal || null;
      return {
        ...badge,
        unlocked: isUn,
        unlockedAt: unlocked[badge.id]?.unlockedAt || null,
        currentProgress: current,
        targetProgress: target
      };
    });
  }

  /**
   * Returns achievements list filtered by brand.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  static getBadgesByBrand(brand = 'all', storage = null) {
    const list = AchievementManager.getAchievements(storage);
    if (!brand || brand === 'all') return list;
    return list.filter(b => (b.brand || 'crazy') === brand);
  }

  /**
   * Returns summary counts and badges grouped by each brand.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getBrandSummary(storage = null) {
    const list = AchievementManager.getAchievements(storage);
    const brands = (typeof GAME_BRANDS !== 'undefined' && Array.isArray(GAME_BRANDS)) ? GAME_BRANDS : [
      { id: 'crazy', name: 'Crazy Cribbage', shortName: 'Crazy', icon: '🃏', color: '#facc15' },
      { id: 'clash', name: 'Cribbage Clash', shortName: 'Clash', icon: '⚔️', color: '#38bdf8' },
      { id: 'pints', name: 'Pints & Pegs', shortName: 'Pints', icon: '🍺', color: '#10b981' },
      { id: 'adult', name: 'Skunked & Stripped', shortName: 'Adult', icon: '🦨', color: '#ec4899' }
    ];

    const summary = {
      overall: {
        total: list.length,
        unlocked: list.filter(b => b.unlocked).length,
        pct: list.length > 0 ? Math.round((list.filter(b => b.unlocked).length / list.length) * 100) : 0
      }
    };

    for (const b of brands) {
      const bList = list.filter(item => (item.brand || 'crazy') === b.id);
      const bUnlocked = bList.filter(item => item.unlocked).length;
      summary[b.id] = {
        ...b,
        badges: bList,
        total: bList.length,
        unlocked: bUnlocked,
        pct: bList.length > 0 ? Math.round((bUnlocked / bList.length) * 100) : 0
      };
    }
    return summary;
  }

  /**
   * Opens the Trophy Room modal dialog with interactive brand tabs, brand sections, and progress tracking.
   * Displays the trophies for each brand clearly separated by brand.
   * @param {string} [defaultBrand='all']
   */
  static showTrophyRoomModal(defaultBrand = 'all') {
    if (typeof document === 'undefined') return;

    // Evaluate milestones before opening to guarantee up-to-date unlock states
    AchievementManager.checkMilestoneBadges();

    const list = AchievementManager.getAchievements();
    const unlockedCount = list.filter(b => b.unlocked).length;
    const totalCount = list.length;
    const pct = Math.round((unlockedCount / totalCount) * 100);

    const brands = (typeof GAME_BRANDS !== 'undefined' && Array.isArray(GAME_BRANDS)) ? GAME_BRANDS : [
      { id: 'crazy', name: 'Crazy Cribbage', shortName: 'Crazy', icon: '🃏', color: '#facc15', badgeBg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.4)' },
      { id: 'clash', name: 'Cribbage Clash', shortName: 'Clash', icon: '⚔️', color: '#38bdf8', badgeBg: 'rgba(56, 189, 248, 0.2)', border: 'rgba(56, 189, 248, 0.4)' },
      { id: 'pints', name: 'Pints & Pegs', shortName: 'Pints', icon: '🍺', color: '#10b981', badgeBg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 0.4)' },
      { id: 'adult', name: 'Skunked & Stripped', shortName: 'Adult', icon: '🦨', color: '#ec4899', badgeBg: 'rgba(244, 63, 94, 0.2)', border: 'rgba(244, 63, 94, 0.4)' }
    ];

    const brandStats = {};
    for (const b of brands) {
      const bList = list.filter(item => (item.brand || 'crazy') === b.id);
      const bUnlocked = bList.filter(item => item.unlocked).length;
      brandStats[b.id] = {
        ...b,
        list: bList,
        total: bList.length,
        unlocked: bUnlocked,
        pct: bList.length > 0 ? Math.round((bUnlocked / bList.length) * 100) : 0
      };
    }

    const modalId = 'trophyRoomModal';
    let overlay = document.getElementById(modalId);
    if (overlay) overlay.remove();

    overlay = document.createElement('div');
    overlay.id = modalId;
    overlay.className = 'modal-backdrop';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.82);
      backdrop-filter: blur(8px);
      z-index: 20000000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
    `;

    const CATEGORIES = [
      { id: 'all', label: `All Badges (${totalCount})` },
      { id: 'tournaments', label: `🏆 Tournaments (${list.filter(b => b.category === 'tournaments').length})` },
      { id: 'daily', label: `🧩 Daily Challenges (${list.filter(b => b.category === 'daily').length})` },
      { id: 'victories', label: `🥇 Victories & Skunks (${list.filter(b => b.category === 'victories').length})` },
      { id: 'hands', label: `✋ High Hands (${list.filter(b => b.category === 'hands').length})` },
      { id: 'specials', label: `🃏 Specials & Rules (${list.filter(b => b.category === 'specials').length})` }
    ];

    const rarityColors = {
      legendary: 'border: 1.5px solid #f59e0b; background: rgba(245, 158, 11, 0.08);',
      epic: 'border: 1.5px solid #a855f7; background: rgba(168, 85, 247, 0.08);',
      rare: 'border: 1.5px solid #3b82f6; background: rgba(59, 130, 246, 0.08);',
      common: 'border: 1.5px solid rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.04);'
    };

    const lockedStyle = 'border: 1.5px dashed rgba(255, 255, 255, 0.12); background: rgba(15, 12, 24, 0.4); opacity: 0.75;';

    const renderCard = (badge) => {
      const isUn = badge.unlocked;
      const bDef = brands.find(b => b.id === (badge.brand || 'crazy')) || brands[0];
      const dateStr = isUn && badge.unlockedAt
        ? new Date(badge.unlockedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;

      const hasTarget = badge.targetProgress != null && badge.targetProgress > 0;
      const curProg = badge.currentProgress || 0;
      const tgtProg = badge.targetProgress || 1;
      const progPct = Math.min(100, Math.round((curProg / tgtProg) * 100));

      return `
        <div class="trophy-badge-card" data-badge-id="${badge.id}" data-brand="${badge.brand || 'crazy'}" data-category="${badge.category || 'all'}" style="
          padding: 14px 16px;
          border-radius: 12px;
          display: flex;
          gap: 14px;
          align-items: flex-start;
          transition: all 0.2s ease;
          ${isUn ? rarityColors[badge.rarity] || rarityColors.common : lockedStyle}
        ">
          <div style="
            font-size: 2.2rem;
            line-height: 1;
            filter: ${isUn ? 'drop-shadow(0 2px 8px rgba(234, 179, 8, 0.4))' : 'grayscale(100%) opacity(35%)'};
            flex-shrink: 0;
          ">
            ${isUn ? badge.icon : '🔒'}
          </div>
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap;">
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <span style="font-size: 1.02rem; font-weight: 700; color: ${isUn ? '#ffffff' : '#94a3b8'};">
                  ${badge.name}
                </span>
                <span class="badge-brand-chip" style="
                  font-size: 0.68rem;
                  font-weight: 700;
                  padding: 2px 7px;
                  border-radius: 6px;
                  background: ${bDef.badgeBg};
                  color: ${bDef.color};
                  border: 1px solid ${bDef.border};
                ">
                  ${bDef.icon} ${bDef.shortName}
                </span>
              </div>
              <span style="
                font-size: 0.68rem;
                font-weight: 800;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                padding: 2px 8px;
                border-radius: 6px;
                background: ${isUn ? 'rgba(234, 179, 8, 0.2)' : 'rgba(255, 255, 255, 0.08)'};
                color: ${isUn ? '#facc15' : '#64748b'};
                flex-shrink: 0;
              ">
                ${badge.rarity}
              </span>
            </div>
            <p style="font-size: 0.82rem; color: ${isUn ? '#cbd5e1' : '#64748b'}; margin: 4px 0 0; line-height: 1.35;">
              ${badge.desc}
            </p>

            ${!isUn && hasTarget ? `
              <div style="margin-top: 8px;">
                <div style="display: flex; justify-content: space-between; font-size: 0.72rem; color: #facc15; font-weight: 700; margin-bottom: 3px;">
                  <span>Progress</span>
                  <span>${curProg} / ${tgtProg} (${progPct}%)</span>
                </div>
                <div style="height: 4px; border-radius: 2px; background: rgba(255, 255, 255, 0.1); width: 100%; overflow: hidden;">
                  <div style="height: 100%; border-radius: 2px; width: ${progPct}%; background: linear-gradient(90deg, #f59e0b, #eab308); transition: width 0.3s ease;"></div>
                </div>
              </div>
            ` : ''}

            ${isUn && dateStr ? `
              <div style="font-size: 0.72rem; color: #94a3b8; margin-top: 6px; font-weight: 600;">
                ★ Unlocked ${dateStr}
              </div>
            ` : ''}
          </div>
        </div>
      `;
    };

    // Render 4 Brand Sections separated by Brand
    const brandSectionsHtml = brands.map(b => {
      const bs = brandStats[b.id];
      const cardsHtml = bs.list.map(renderCard).join('');
      return `
        <div class="trophy-brand-section" data-brand-section="${b.id}" style="margin-bottom: 24px;">
          <!-- Brand Section Banner -->
          <div class="trophy-brand-banner" style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
            background: ${b.badgeBg};
            border: 1.5px solid ${b.border};
            border-radius: 12px;
            margin-bottom: 12px;
            flex-wrap: wrap;
            gap: 10px;
          ">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 1.6rem;">${b.icon}</span>
              <div>
                <div style="font-size: 1.12rem; font-weight: 800; color: ${b.color};">${b.name} Trophies</div>
                <div style="font-size: 0.74rem; color: #cbd5e1;">${bs.total} Trophies Available</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <div style="text-align: right;">
                <div style="font-size: 0.9rem; font-weight: 800; color: #facc15;">
                  ${bs.unlocked} of ${bs.total} Unlocked
                </div>
                <div style="font-size: 0.72rem; color: #94a3b8;">${bs.pct}% Complete</div>
              </div>
              <div style="width: 76px; height: 7px; background: rgba(255, 255, 255, 0.12); border-radius: 4px; overflow: hidden;">
                <div style="width: ${bs.pct}%; height: 100%; background: ${b.color}; border-radius: 4px; transition: width 0.3s ease;"></div>
              </div>
            </div>
          </div>

          <!-- Cards List for this Brand -->
          <div class="trophy-brand-cards" style="display: flex; flex-direction: column; gap: 10px;">
            ${cardsHtml}
          </div>
        </div>
      `;
    }).join('');

    // Brand navigation tabs
    const brandTabsHtml = `
      <button type="button" class="trophy-brand-tab ${defaultBrand === 'all' ? 'active' : ''}" data-brand="all" style="
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 16px;
        font-size: 0.84rem;
        font-weight: 700;
        border-radius: 20px;
        cursor: pointer;
        border: 1.5px solid ${defaultBrand === 'all' ? '#facc15' : 'rgba(255, 255, 255, 0.15)'};
        background: ${defaultBrand === 'all' ? 'rgba(234, 179, 8, 0.25)' : 'rgba(255, 255, 255, 0.05)'};
        color: ${defaultBrand === 'all' ? '#facc15' : '#cbd5e1'};
        white-space: nowrap;
        flex-shrink: 0;
        min-height: 34px;
        box-sizing: border-box;
        transition: all 0.15s ease;
      ">
        <span>🌐 All Brands (${unlockedCount}/${totalCount})</span>
      </button>
      ${brands.map(b => {
        const bs = brandStats[b.id];
        const isAct = defaultBrand === b.id;
        return `
          <button type="button" class="trophy-brand-tab ${isAct ? 'active' : ''}" data-brand="${b.id}" style="
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 7px 16px;
            font-size: 0.84rem;
            font-weight: 700;
            border-radius: 20px;
            cursor: pointer;
            border: 1.5px solid ${isAct ? b.color : 'rgba(255, 255, 255, 0.15)'};
            background: ${isAct ? b.badgeBg : 'rgba(255, 255, 255, 0.05)'};
            color: ${isAct ? b.color : '#cbd5e1'};
            white-space: nowrap;
            flex-shrink: 0;
            min-height: 34px;
            box-sizing: border-box;
            transition: all 0.15s ease;
          ">
            <span>${b.icon} ${b.shortName} (${bs.unlocked}/${bs.total})</span>
          </button>
        `;
      }).join('')}
    `;

    // Category secondary filter tabs
    const categoryTabsHtml = CATEGORIES.map((cat, idx) => `
      <button type="button" class="trophy-category-tab ${idx === 0 ? 'active' : ''}" data-cat="${cat.id}" style="
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 5px 12px;
        font-size: 0.78rem;
        font-weight: 600;
        border-radius: 16px;
        cursor: pointer;
        border: 1px solid ${idx === 0 ? '#facc15' : 'rgba(255, 255, 255, 0.12)'};
        background: ${idx === 0 ? 'rgba(234, 179, 8, 0.18)' : 'rgba(255, 255, 255, 0.03)'};
        color: ${idx === 0 ? '#facc15' : '#94a3b8'};
        white-space: nowrap;
        flex-shrink: 0;
        min-height: 28px;
        box-sizing: border-box;
        transition: all 0.15s ease;
      ">
        ${cat.label}
      </button>
    `).join('');

    overlay.innerHTML = `
      <div style="
        background: linear-gradient(145deg, #181424, #0f0c18);
        border: 1px solid rgba(212, 160, 23, 0.35);
        border-radius: 16px;
        max-width: 860px;
        width: 100%;
        max-height: 90vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.85), 0 0 35px rgba(212, 160, 23, 0.18);
        color: #f8fafc;
        overflow: hidden;
      ">
        <!-- Header -->
        <div style="padding: 16px 24px; border-bottom: 1px solid rgba(255, 255, 255, 0.1); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; flex-shrink: 0;">
          <div>
            <h2 style="margin: 0; font-size: 1.4rem; font-weight: 800; color: #facc15; display: flex; align-items: center; gap: 8px;">
              🏆 Trophy Room &amp; Hall of Fame
            </h2>
            <p style="margin: 4px 0 0; font-size: 0.85rem; color: #94a3b8;">
              ${unlockedCount} of ${totalCount} Badges Unlocked (${pct}%) · Separated by Game Brand
            </p>
          </div>
          <button id="btnCloseTrophyRoom" style="
            background: rgba(255, 255, 255, 0.08);
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            font-size: 1.2rem;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          ">✕</button>
        </div>

        <!-- Progress bar -->
        <div style="height: 4px; background: rgba(255, 255, 255, 0.08); width: 100%; flex-shrink: 0;">
          <div style="height: 100%; width: ${pct}%; background: linear-gradient(90deg, #f59e0b, #eab308); transition: width 0.3s ease;"></div>
        </div>

        <style>
          .trophy-brand-tabs-row {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 8px 10px !important;
            padding: 12px 24px 8px !important;
            flex-shrink: 0 !important;
            min-height: 52px !important;
            box-sizing: border-box !important;
            background: rgba(0, 0, 0, 0.35) !important;
            align-items: center !important;
          }
          .trophy-category-tabs-row {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 6px 8px !important;
            padding: 6px 24px 10px !important;
            flex-shrink: 0 !important;
            box-sizing: border-box !important;
            background: rgba(0, 0, 0, 0.35) !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
            align-items: center !important;
          }
          #trophyBadgesList::-webkit-scrollbar,
          .trophy-brand-tabs-row::-webkit-scrollbar {
            width: 10px;
            height: 8px;
          }
          #trophyBadgesList::-webkit-scrollbar-track,
          .trophy-brand-tabs-row::-webkit-scrollbar-track {
            background: rgba(0, 0, 0, 0.35);
            border-radius: 8px;
            margin: 4px;
            box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.6);
          }
          #trophyBadgesList::-webkit-scrollbar-thumb,
          .trophy-brand-tabs-row::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #ffe680 0%, #f0c64a 50%, #c99718 100%);
            border-radius: 8px;
            border: 2px solid #1a1528;
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 2px 5px rgba(0, 0, 0, 0.5);
            transition: background 0.15s ease, box-shadow 0.15s ease;
          }
          #trophyBadgesList::-webkit-scrollbar-thumb:hover,
          .trophy-brand-tabs-row::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #fff3a8 0%, #ffd700 50%, #e0aa1b 100%);
            box-shadow: 0 0 10px rgba(240, 198, 74, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.6);
          }
        </style>

        <!-- Brand Tabs Navigation (Separated by Brand) -->
        <div class="trophy-brand-tabs-row">
          ${brandTabsHtml}
        </div>

        <!-- Secondary Category Filter Tabs -->
        <div class="trophy-category-tabs-row">
          ${categoryTabsHtml}
        </div>

        <!-- Badges Grid/List (Grouped into Brand Sections) -->
        <div id="trophyBadgesList" class="trophy-badges-list" style="
          padding: 18px 24px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          flex: 1;
          scrollbar-width: thin;
          scrollbar-color: #f0c64a rgba(0, 0, 0, 0.35);
        ">
          ${brandSectionsHtml}
        </div>

        <!-- Footer -->
        <div style="padding: 14px 24px; border-top: 1px solid rgba(255, 255, 255, 0.1); display: flex; justify-content: flex-end; background: rgba(0, 0, 0, 0.25);">
          <button id="btnCloseTrophyRoomBottom" class="btn-gold" style="padding: 8px 24px; font-weight: 700; border-radius: 8px; cursor: pointer;">
            Close
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Active filters state
    let activeBrandFilter = defaultBrand || 'all';
    let activeCategoryFilter = 'all';

    const brandTabs = overlay.querySelectorAll('.trophy-brand-tab');
    const categoryTabs = overlay.querySelectorAll('.trophy-category-tab');
    const brandSections = overlay.querySelectorAll('.trophy-brand-section');
    const badgeCards = overlay.querySelectorAll('.trophy-badge-card');

    const updateFilterVisibility = () => {
      brandSections.forEach(sec => {
        const secBrand = sec.getAttribute('data-brand-section');
        const isBrandMatch = (activeBrandFilter === 'all' || secBrand === activeBrandFilter);
        if (isBrandMatch) {
          sec.style.display = 'block';
        } else {
          sec.style.display = 'none';
        }
      });

      badgeCards.forEach(card => {
        const cardCat = card.getAttribute('data-category');
        const cardBrand = card.getAttribute('data-brand');
        const isBrandMatch = (activeBrandFilter === 'all' || cardBrand === activeBrandFilter);
        const isCatMatch = (activeCategoryFilter === 'all' || cardCat === activeCategoryFilter);

        if (isBrandMatch && isCatMatch) {
          card.style.display = 'flex';
        } else {
          card.style.display = 'none';
        }
      });
    };

    // Setup interactive brand tabs
    brandTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetBrand = tab.getAttribute('data-brand');
        activeBrandFilter = targetBrand;

        brandTabs.forEach(t => {
          t.style.border = '1.5px solid rgba(255, 255, 255, 0.15)';
          t.style.background = 'rgba(255, 255, 255, 0.05)';
          t.style.color = '#cbd5e1';
          t.classList.remove('active');
        });

        const bDef = brands.find(b => b.id === targetBrand);
        tab.style.border = `1.5px solid ${bDef ? bDef.color : '#facc15'}`;
        tab.style.background = bDef ? bDef.badgeBg : 'rgba(234, 179, 8, 0.25)';
        tab.style.color = bDef ? bDef.color : '#facc15';
        tab.classList.add('active');

        updateFilterVisibility();
      });
    });

    // Setup interactive category tabs
    categoryTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        activeCategoryFilter = tab.getAttribute('data-cat');

        categoryTabs.forEach(t => {
          t.style.border = '1px solid rgba(255, 255, 255, 0.12)';
          t.style.background = 'rgba(255, 255, 255, 0.03)';
          t.style.color = '#94a3b8';
          t.classList.remove('active');
        });
        tab.style.border = '1px solid #facc15';
        tab.style.background = 'rgba(234, 179, 8, 0.18)';
        tab.style.color = '#facc15';
        tab.classList.add('active');

        updateFilterVisibility();
      });
    });

    // Apply initial filter if defaultBrand was specified
    if (defaultBrand && defaultBrand !== 'all') {
      updateFilterVisibility();
    }

    const closeFn = () => overlay.remove();
    const btnX = overlay.querySelector('#btnCloseTrophyRoom');
    const btnBottom = overlay.querySelector('#btnCloseTrophyRoomBottom');
    if (btnX) btnX.onclick = closeFn;
    if (btnBottom) btnBottom.onclick = closeFn;
    overlay.onclick = (e) => {
      if (e.target === overlay) closeFn();
    };
  }
}
